import { Badge, Button, Grid, Group, Paper, Stack, Text, Title } from "@mantine/core";
import { IconSend2 } from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { SubmissionPrepModal } from "../../components/candidates/SubmissionPrepModal";
import { JobPickerModal } from "../../components/explore/JobPickerModal";
import { MatchedJobsSection } from "../../components/matching/MatchedJobsSection";
import { ContactActions } from "../../components/matching/ContactActions";
import { ProfileKeyFacts } from "../../components/profile/ProfileKeyFacts";
import { ProfileRelatedEntities } from "../../components/profile/ProfileRelatedEntities";
import { ProfileShell } from "../../components/profile/ProfileShell";
import { SubmitCandidateModal, SUBMIT_TO_JOB_BUTTON } from "../../components/SubmitCandidateModal";
import { LoadingOverlay } from "../../components/ui";
import { ProfileTimeline } from "../../components/profile/ProfileTimeline";
import { ResumeLink } from "../../components/ResumeLink";
import { formatCities, formatDate, formatExperience, formatAvailabilityDays, formatPaise, formatRelativeTime, candidateSourceLabel, ACTOR_LABEL_RECRUITER } from "../../lib/format";
import { profilesApi } from "../../lib/api";
import { axioTokens } from "../../theme";
import type { Job } from "../../types/api";

const PREP_GUIDE_LABEL = "Submission prep guide";
const EMPTY_FACT = "—";
const HR_PATH_PREFIX = "/hr";
const NO_PHONE_LABEL = "No phone";

function backTo(pathname: string) {
  if (pathname.startsWith("/admin")) return "/admin/applications";
  if (pathname.startsWith("/hr")) return "/hr/submissions";
  if (pathname.startsWith("/consultancy")) return "/consultancy/candidates";
  return "/candidates";
}

export function CandidateProfilePage() {
  const { candidateId } = useParams<{ candidateId: string }>();
  const location = useLocation();
  const [prepOpen, setPrepOpen] = useState(false);
  const [jobPickerOpen, setJobPickerOpen] = useState(false);
  const [submitJobId, setSubmitJobId] = useState<string | null>(null);
  const isHrView = location.pathname.startsWith(HR_PATH_PREFIX);
  const isRecruiterView =
    !location.pathname.startsWith("/admin") &&
    !location.pathname.startsWith(HR_PATH_PREFIX) &&
    !location.pathname.startsWith("/consultancy");
  const profileQuery = useQuery({
    queryKey: ["candidate-profile", candidateId],
    queryFn: () => profilesApi.getCandidateProfile(candidateId!),
    enabled: !!candidateId,
  });
  const timelineQuery = useQuery({
    queryKey: ["candidate-profile-timeline", candidateId],
    queryFn: () => profilesApi.getCandidateTimeline(candidateId!, { page_size: 100 }),
    enabled: !!candidateId,
  });

  if (profileQuery.isLoading || timelineQuery.isLoading) {
    return <LoadingOverlay height="var(--axio-unit-280)" />;
  }
  if (!profileQuery.data) {
    return <Text c="dimmed">Candidate profile not found.</Text>;
  }
  const profile = profileQuery.data;
  return (
    <Stack gap="md">
      <Button component={Link} to={backTo(location.pathname)} variant="subtle" w="fit-content">
        ← Back
      </Button>
      <ProfileShell
        name={profile.full_name}
        showSubtitleIcon={false}
        subtitle={
          <ContactActions
            phone={profile.phone}
            phoneLabel={profile.phone || NO_PHONE_LABEL}
            email={profile.email}
            candidateId={profile.candidate_id}
            enableLog={isRecruiterView}
          />
        }
        role="candidate"
        status={profile.consent_given ? "consented" : "no_consent"}
        actions={
          isRecruiterView ? (
            <Group gap="sm">
              <Button variant="default" onClick={() => setPrepOpen(true)}>
                {PREP_GUIDE_LABEL}
              </Button>
              <Button
                color="violet"
                leftSection={<IconSend2 size="var(--axio-unit-16)" />}
                onClick={() => {
                  setSubmitJobId(null);
                  setJobPickerOpen(true);
                }}
              >
                {SUBMIT_TO_JOB_BUTTON}
              </Button>
            </Group>
          ) : undefined
        }
        stats={
          <ProfileKeyFacts
            facts={[
              { label: "Registered", value: formatDate(profile.created_at) },
              { label: "Source", value: candidateSourceLabel(profile.source) },
              { label: "Submissions", value: String(profile.submission_count ?? profile.submissions.length) },
              { label: "Last contacted", value: profile.last_contacted_at ? formatRelativeTime(profile.last_contacted_at) : "—" },
            ]}
          />
        }
        timeline={<ProfileTimeline entries={timelineQuery.data?.items ?? []} />}
        related={<ProfileRelatedEntities submissions={profile.submissions} />}
        beforeTabs={
          isRecruiterView && candidateId ? (
            <MatchedJobsSection
              candidateId={candidateId}
              onSubmit={(jobId) => setSubmitJobId(jobId)}
            />
          ) : undefined
        }
        details={
          <Stack gap={axioTokens.unit[32]}>
            <Grid>
              <Grid.Col span={{ base: 12, md: 6 }}>
                <Paper withBorder p="lg">
                  <Title order={5} mb="md">
                    Candidate summary
                  </Title>
                  <Stack gap="sm">
                    {!isHrView && (
                      <Text size="sm">
                        {ACTOR_LABEL_RECRUITER}: {profile.recruiter_name ?? EMPTY_FACT}
                      </Text>
                    )}
                    <Text size="sm">Cities: {formatCities(profile.cities)}</Text>
                    <Text size="sm">Experience: {formatExperience(profile.years_experience)}</Text>
                    <Text size="sm">Notice: {formatAvailabilityDays(profile.availability_in_days)}</Text>
                    <Text size="sm">
                      Current CTC: {formatPaise(profile.current_ctc_paise, profile.currency ?? "INR")}
                    </Text>
                    <Text size="sm">
                      Expected CTC: {formatPaise(profile.expected_ctc_paise, profile.currency ?? "INR")}
                    </Text>
                    <Text size="sm">
                      Consent at: {profile.consent_at ? formatDate(profile.consent_at) : "Not recorded"}
                    </Text>
                    {profile.linkedin_url && (
                      <Text size="sm">
                        LinkedIn:{" "}
                        <Text
                          component="a"
                          href={profile.linkedin_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          span
                          td="underline"
                        >
                          Open profile
                        </Text>
                      </Text>
                    )}
                    <ResumeLink url={profile.resume_url} />
                    {profile.languages.length > 0 && (
                      <Text size="sm">Languages: {profile.languages.join(", ")}</Text>
                    )}
                  </Stack>
                </Paper>
              </Grid.Col>
              <Grid.Col span={{ base: 12, md: 6 }}>
                <Paper withBorder p="lg">
                  <Title order={5} mb="md">
                    Skills
                  </Title>
                  <Stack gap="sm">
                    {profile.skills.length === 0 ? (
                      <Text size="sm" c="dimmed">
                        No skills recorded.
                      </Text>
                    ) : (
                      <Group gap="xs">
                        {profile.skills.map((skill) => (
                          <Badge key={skill} variant="outline" color="gray">
                            {skill}
                          </Badge>
                        ))}
                      </Group>
                    )}
                  </Stack>
                </Paper>
              </Grid.Col>
            </Grid>
          </Stack>
        }
      />
      {isRecruiterView && candidateId ? (
        <>
          <SubmissionPrepModal opened={prepOpen} onClose={() => setPrepOpen(false)} candidateId={candidateId} />
          <JobPickerModal
            opened={jobPickerOpen}
            onClose={() => setJobPickerOpen(false)}
            onPick={(job: Job) => setSubmitJobId(job.id)}
          />
          <SubmitCandidateModal
            jobId={submitJobId ?? ""}
            opened={!!submitJobId}
            candidateId={candidateId}
            candidateName={profile.full_name}
            onClose={() => setSubmitJobId(null)}
          />
        </>
      ) : null}
    </Stack>
  );
}